import { Controller } from "@hotwired/stimulus"
import { apiGet, apiPost, apiDelete, formatMoney, decimalsFor, t } from "../api_helper"
import { createTransactionOffline } from "../lib/offline_api"
import { offlineStore } from "../lib/offline_store"
import { networkStatus } from "../lib/network_status"
import { alertOffline } from "../lib/offline_ui"
import "../lib/paywall"

export default class extends Controller {
  static targets = [
    "amount", "note", "categoriesGrid", "expenseBtn", "incomeBtn", "transferBtn",
    "saveBtn", "successOverlay", "successText",
    "accountPills", "accountSection", "transferSection", "transferFromCards", "transferToCards",
    "transferCurrencyPills", "conversionHint",
    "categorySection", "noteSection", "exprResult",
    "undoBtn", "undoTimer",
    "pickerToggleSection", "categoryTabBtn", "debtTabBtn",
    "debtSection", "debtsList"
  ]

  connect() {
    this.kind = "expense"
    this.selectedCategoryId = null
    this.selectedAccountId = null
    this.accounts = []
    this.rates = {}
    this.transferInputCurrency = null
    this.lastTransactionId = null
    this.undoCountdown = null
    this.redirectTimer = null
    this.pickerMode = "category"
    this.selectedDebtId = null
    this.debts = []
    this.loadCategories()
    this.loadAccounts()
  }

  disconnect() {
    if (this.undoCountdown) clearInterval(this.undoCountdown)
    if (this.redirectTimer) clearTimeout(this.redirectTimer)
  }

  async loadAccounts() {
    try {
      const data = await apiGet("/api/accounts")
      this.accounts = data.accounts
      this.rates = data.rates || {}
      this.defaultCurrency = data.default_currency || "RUB"
      offlineStore.cacheData("cached_accounts", this.accounts)
    } catch (e) {
      console.error("Accounts load error:", e)
      if (e.status === 403) {
        window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "accounts" } }))
        return
      }
      await this.loadAccountsFromCache()
    }
    const defaultAcc = this.accounts.find(a => a.is_default)
    this.selectedAccountId = defaultAcc ? defaultAcc.id : (this.accounts[0]?.id || null)
    this.renderAccounts()
  }

  async loadAccountsFromCache() {
    const cached = await offlineStore.getCachedData("cached_accounts")
    if (cached.length) {
      this.accounts = cached
      this.defaultCurrency = cached[0]?.currency || "RUB"
    }
    const defaultAcc = this.accounts.find(a => a.is_default)
    this.selectedAccountId = defaultAcc ? defaultAcc.id : (this.accounts[0]?.id || null)
    this.renderAccounts()
  }

  convertAmount(amount, from, to) {
    if (from === to || !amount) return amount
    const fromRate = this.rates[from] || 1
    const toRate = this.rates[to] || 1
    const result = amount * fromRate / toRate
    const decimals = decimalsFor(to)
    const factor = Math.pow(10, decimals)
    return Math.round(result * factor) / factor
  }

  renderAccounts() {
    if (!this.hasAccountPillsTarget || !this.accounts.length) return

    this.accountPillsTarget.innerHTML = this.accounts.map(a => {
      const selected = this.selectedAccountId === a.id
      const bg = selected
        ? (a.color || "rgb(99, 102, 241)")
        : `color-mix(in srgb, ${a.color || '#3B82F6'} 12%, transparent)`
      const color = selected ? "#fff" : "inherit"
      return `
        <button type="button"
                data-action="click->pwa-add-transaction#selectAccount"
                data-account-id="${a.id}"
                class="flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all touch-active whitespace-nowrap"
                style="background-color: ${bg}; color: ${color};">
          ${a.icon} ${a.name}
        </button>`
    }).join("")
  }

  renderTransferSelects() {
    if (!this.hasTransferFromCardsTarget || !this.hasTransferToCardsTarget) return

    if (!this.transferFromId) {
      const defaultAcc = this.accounts.find(a => a.is_default) || this.accounts[0]
      this.transferFromId = defaultAcc?.id
      const secondAcc = this.accounts.find(a => a.id !== this.transferFromId)
      this.transferToId = secondAcc?.id || this.accounts[0]?.id
    }

    const fromAcc = this.accounts.find(a => a.id === this.transferFromId)
    if (!this.transferInputCurrency) {
      this.transferInputCurrency = fromAcc?.currency || this.defaultCurrency
    }

    this.transferFromCardsTarget.innerHTML = this.renderAccountCards(this.transferFromId, "from")
    this.transferToCardsTarget.innerHTML = this.renderAccountCards(this.transferToId, "to")
    this.renderCurrencyPills()
    this.updateConversion()
  }

  renderAccountCards(selectedId, direction) {
    return this.accounts.map(a => {
      const selected = selectedId === a.id
      const bg = selected
        ? (a.color || "rgb(99, 102, 241)")
        : `color-mix(in srgb, ${a.color || '#3B82F6'} 12%, transparent)`
      const color = selected ? "#fff" : "inherit"
      const hintColor = selected ? "rgba(255,255,255,0.8)" : "rgb(148, 163, 184)"
      return `
        <button type="button"
                data-action="click->pwa-add-transaction#selectTransferAccount"
                data-account-id="${a.id}" data-direction="${direction}"
                class="flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-full touch-active transition-all whitespace-nowrap"
                style="background-color: ${bg};">
          <span style="font-size: 16px;">${a.icon}</span>
          <span class="text-sm font-medium" style="color: ${color};">${a.name}</span>
          <span class="text-xs font-semibold" style="color: ${hintColor};">${formatMoney(a.balance, a.currency)}</span>
        </button>`
    }).join("")
  }

  renderCurrencyPills() {
    if (!this.hasTransferCurrencyPillsTarget) return

    const fromAcc = this.accounts.find(a => a.id === this.transferFromId)
    const toAcc = this.accounts.find(a => a.id === this.transferToId)

    const currencies = [...new Set([fromAcc?.currency, toAcc?.currency].filter(Boolean))]

    if (currencies.length <= 1) {
      this.transferCurrencyPillsTarget.innerHTML = ""
      return
    }

    this.transferCurrencyPillsTarget.innerHTML = currencies.map(c => {
      const selected = this.transferInputCurrency === c
      const bg = selected ? "rgb(99, 102, 241)" : "rgb(248, 250, 252)"
      const color = selected ? "#fff" : "inherit"
      return `
        <button type="button"
                data-action="click->pwa-add-transaction#selectTransferCurrency"
                data-currency="${c}"
                class="px-4 py-1.5 rounded-full text-xs font-semibold transition-all touch-active"
                style="background-color: ${bg}; color: ${color};">
          ${c}
        </button>`
    }).join("")
  }

  selectTransferCurrency(event) {
    this.transferInputCurrency = event.currentTarget.dataset.currency
    this.renderCurrencyPills()
    this.updateConversion()
  }

  updateConversion() {
    if (!this.hasConversionHintTarget) return

    const amount = this.getAmount()
    const fromAcc = this.accounts.find(a => a.id === this.transferFromId)
    const toAcc = this.accounts.find(a => a.id === this.transferToId)

    if (!amount || amount <= 0 || !fromAcc || !toAcc) {
      this.conversionHintTarget.innerHTML = ""
      return
    }

    const inputCur = this.transferInputCurrency || fromAcc.currency
    const fromCur = fromAcc.currency
    const toCur = toAcc.currency

    let lines = []

    const fromAmount = this.convertAmount(amount, inputCur, fromCur)
    const toAmount = this.convertAmount(amount, inputCur, toCur)

    lines.push(`<span style="color: rgb(239, 68, 68);">-${formatMoney(fromAmount, fromCur)}</span>`)
    lines.push(`<span style="color: rgb(16, 185, 129);">+${formatMoney(toAmount, toCur)}</span>`)

    this.conversionHintTarget.innerHTML = lines.join(`<span style="margin: 0 8px; opacity: 0.5;">→</span>`)
  }

  selectTransferAccount(event) {
    const id = parseInt(event.currentTarget.dataset.accountId)
    const direction = event.currentTarget.dataset.direction

    if (direction === "from") {
      this.transferFromId = id
      if (this.transferToId === id) {
        const other = this.accounts.find(a => a.id !== id)
        if (other) this.transferToId = other.id
      }
      const acc = this.accounts.find(a => a.id === id)
      this.transferInputCurrency = acc?.currency || this.defaultCurrency
    } else {
      this.transferToId = id
      if (this.transferFromId === id) {
        const other = this.accounts.find(a => a.id !== id)
        if (other) this.transferFromId = other.id
      }
    }

    this.renderTransferSelects()
  }

  swapAccounts() {
    const tmp = this.transferFromId
    this.transferFromId = this.transferToId
    this.transferToId = tmp
    this.renderTransferSelects()
  }

  selectAccount(event) {
    this.selectedAccountId = parseInt(event.currentTarget.dataset.accountId)
    this.renderAccounts()
  }

  async loadCategories() {
    try {
      const categories = await apiGet("/api/categories")
      this.categories = categories
      offlineStore.cacheData("cached_categories", categories)
    } catch (e) {
      console.error("Categories load error:", e)
      if (e.status === 403) {
        window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "categories" } }))
        return
      }
      await this.loadCategoriesFromCache()
    }
    this.renderCategories()
  }

  async loadCategoriesFromCache() {
    const cached = await offlineStore.getCachedData("cached_categories")
    if (cached.length) {
      this.categories = cached
    }
    this.renderCategories()
  }

  renderCategories() {
    const filtered = (this.categories || []).filter(c => c.kind === this.kind)
    if (!this.hasCategoriesGridTarget) return

    this.categoriesGridTarget.innerHTML = filtered.map(c => {
      const selected = this.selectedCategoryId === c.id
      const ringStyle = selected ? `box-shadow: 0 0 0 2px ${c.color}; transform: scale(1.05);` : ""
      const catBg = c.color ? `color-mix(in srgb, ${c.color} 10%, transparent)` : "rgb(248, 250, 252)"
      return `
        <button type="button"
                data-action="click->pwa-add-transaction#selectCategory"
                data-category-id="${c.id}"
                class="flex flex-col items-center p-2 rounded-2xl transition-all touch-active"
                style="${ringStyle}">
          <div class="flex items-center justify-center mb-1.5"
               style="width: 44px; height: 44px; border-radius: 50%; background-color: ${catBg}; font-size: 22px;">
            ${c.icon}
          </div>
          <span class="text-xs font-medium truncate w-full text-center" style="${selected ? `color: ${c.color}; font-weight: 700;` : ''}">${c.name}</span>
        </button>`
    }).join("")
  }

  selectCategory(event) {
    const btn = event.currentTarget
    this.selectedCategoryId = parseInt(btn.dataset.categoryId)
    this.selectedDebtId = null
    this.renderCategories()
  }

  // Debt picker methods

  showCategoryPicker() {
    this.pickerMode = "category"
    this.selectedDebtId = null
    this.updatePickerToggleUI()
    if (this.hasCategorySectionTarget) this.categorySectionTarget.classList.remove("hidden")
    if (this.hasDebtSectionTarget) this.debtSectionTarget.classList.add("hidden")
  }

  showDebtPicker() {
    this.pickerMode = "debt"
    this.selectedCategoryId = null
    this.updatePickerToggleUI()
    if (this.hasCategorySectionTarget) this.categorySectionTarget.classList.add("hidden")
    if (this.hasDebtSectionTarget) this.debtSectionTarget.classList.remove("hidden")
    if (!this.debts.length) this.loadDebts()
    else this.renderDebts()
  }

  updatePickerToggleUI() {
    if (this.hasCategoryTabBtnTarget) {
      if (this.pickerMode === "category") {
        this.categoryTabBtnTarget.className = "px-4 py-1.5 rounded-full text-xs font-semibold transition-all bg-indigo-500 text-white"
      } else {
        this.categoryTabBtnTarget.className = "px-4 py-1.5 rounded-full text-xs font-semibold transition-all text-slate-400"
      }
    }
    if (this.hasDebtTabBtnTarget) {
      if (this.pickerMode === "debt") {
        this.debtTabBtnTarget.className = "px-4 py-1.5 rounded-full text-xs font-semibold transition-all bg-indigo-500 text-white"
      } else {
        this.debtTabBtnTarget.className = "px-4 py-1.5 rounded-full text-xs font-semibold transition-all text-slate-400"
      }
    }
  }

  async loadDebts() {
    try {
      const data = await apiGet("/api/debts")
      this.debts = (data.debts || []).filter(d => !d.settled)
      this.renderDebts()
    } catch (e) {
      console.error("Debts load error:", e)
      if (this.hasDebtsListTarget) {
        this.debtsListTarget.innerHTML = `<div class="text-center text-sm py-4 text-slate-400">${t("add_transaction.no_active_debts")}</div>`
      }
    }
  }

  renderDebts() {
    if (!this.hasDebtsListTarget) return

    if (!this.debts.length) {
      this.debtsListTarget.innerHTML = `<div class="text-center text-sm py-4 text-slate-400">${t("add_transaction.no_active_debts")}</div>`
      return
    }

    this.debtsListTarget.innerHTML = this.debts.map(d => {
      const selected = this.selectedDebtId === d.id
      const dirLabel = d.direction === "i_owe" ? t("debts.i_owe") : t("debts.owed_to_me")
      const dirColor = d.direction === "i_owe" ? "rgb(239, 68, 68)" : "rgb(16, 185, 129)"
      const bg = selected ? "rgb(99, 102, 241)" : "rgb(248, 250, 252)"
      const textColor = selected ? "#fff" : "inherit"
      const hintColor = selected ? "rgba(255,255,255,0.7)" : "rgb(148, 163, 184)"
      const ringStyle = selected ? "box-shadow: 0 0 0 2px rgb(99, 102, 241);" : ""
      return `
        <button type="button"
                data-action="click->pwa-add-transaction#selectDebt"
                data-debt-id="${d.id}"
                class="flex items-center justify-between w-full px-4 py-3 rounded-2xl transition-all touch-active dark:bg-slate-800/50"
                style="background-color: ${bg}; ${ringStyle}">
          <div class="flex flex-col items-start">
            <span class="text-sm font-semibold" style="color: ${textColor};">${d.person_name}</span>
            <span class="text-xs" style="color: ${selected ? hintColor : dirColor};">${dirLabel}</span>
          </div>
          <div class="text-right">
            <div class="text-sm font-semibold" style="color: ${textColor};">${formatMoney(d.remaining, d.currency)}</div>
            <div class="text-xs" style="color: ${hintColor};">${t("add_transaction.remaining")}</div>
          </div>
        </button>`
    }).join("")
  }

  selectDebt(event) {
    this.selectedDebtId = parseInt(event.currentTarget.dataset.debtId)
    this.selectedCategoryId = null
    this.renderDebts()
  }

  setExpense() {
    this.kind = "expense"
    this.selectedCategoryId = null
    this.updateKindUI()
    this.renderCategories()
    this.showTransactionMode()
  }

  setIncome() {
    this.kind = "income"
    this.selectedCategoryId = null
    this.updateKindUI()
    this.renderCategories()
    this.showTransactionMode()
  }

  setTransfer() {
    this.kind = "transfer"
    this.updateKindUI()
    this.showTransferMode()
  }

  showTransactionMode() {
    if (this.hasAccountSectionTarget) this.accountSectionTarget.classList.remove("hidden")
    if (this.hasTransferSectionTarget) this.transferSectionTarget.classList.add("hidden")
    if (this.hasPickerToggleSectionTarget) this.pickerToggleSectionTarget.classList.remove("hidden")
    if (this.pickerMode === "category") {
      if (this.hasCategorySectionTarget) this.categorySectionTarget.classList.remove("hidden")
      if (this.hasDebtSectionTarget) this.debtSectionTarget.classList.add("hidden")
    } else {
      if (this.hasCategorySectionTarget) this.categorySectionTarget.classList.add("hidden")
      if (this.hasDebtSectionTarget) this.debtSectionTarget.classList.remove("hidden")
    }
    if (this.hasNoteSectionTarget) this.noteSectionTarget.classList.remove("hidden")
  }

  showTransferMode() {
    if (this.hasAccountSectionTarget) this.accountSectionTarget.classList.add("hidden")
    if (this.hasTransferSectionTarget) this.transferSectionTarget.classList.remove("hidden")
    if (this.hasPickerToggleSectionTarget) this.pickerToggleSectionTarget.classList.add("hidden")
    if (this.hasCategorySectionTarget) this.categorySectionTarget.classList.add("hidden")
    if (this.hasDebtSectionTarget) this.debtSectionTarget.classList.add("hidden")
    if (this.hasNoteSectionTarget) this.noteSectionTarget.classList.add("hidden")
    this.renderTransferSelects()
  }

  updateKindUI() {
    const expense = this.hasExpenseBtnTarget ? this.expenseBtnTarget : null
    const income = this.hasIncomeBtnTarget ? this.incomeBtnTarget : null
    const transfer = this.hasTransferBtnTarget ? this.transferBtnTarget : null

    ;[expense, income, transfer].forEach(btn => {
      if (btn) {
        btn.style.backgroundColor = "transparent"
        btn.style.color = "rgb(148, 163, 184)"
      }
    })

    if (this.kind === "expense" && expense) {
      expense.style.backgroundColor = "rgb(239, 68, 68)"
      expense.style.color = "#fff"
    } else if (this.kind === "income" && income) {
      income.style.backgroundColor = "rgb(16, 185, 129)"
      income.style.color = "#fff"
    } else if (this.kind === "transfer" && transfer) {
      transfer.style.backgroundColor = "rgb(99, 102, 241)"
      transfer.style.color = "#fff"
    }
  }

  evaluateExpression(expr) {
    const sanitized = expr.replace(/\s/g, "").replace(/,/g, ".")
    if (!/^[\d.]+([+\-*/][\d.]+)*$/.test(sanitized)) return null
    try {
      const result = Function(`"use strict"; return (${sanitized})`)()
      if (!isFinite(result) || result < 0) return null
      return Math.round(result * 100) / 100
    } catch {
      return null
    }
  }

  isExpression(value) {
    return /[+\-*/]/.test(value.replace(/^\-/, ""))
  }

  getAmount() {
    const raw = this.amountTarget.value.trim()
    if (!raw) return 0
    if (this.isExpression(raw)) {
      return this.evaluateExpression(raw) || 0
    }
    return parseFloat(raw.replace(/,/g, ".")) || 0
  }

  insertOperator(event) {
    const opMap = { "+": "+", "\u2212": "-", "\u00d7": "*", "\u00f7": "/" }
    const op = opMap[event.currentTarget.dataset.operator] || event.currentTarget.dataset.operator
    const input = this.amountTarget
    const pos = input.selectionStart || input.value.length
    const value = input.value
    if ((!value && op !== "-") || /[+\-*/]$/.test(value)) return
    input.value = value.slice(0, pos) + op + value.slice(pos)
    input.focus()
    input.setSelectionRange(pos + 1, pos + 1)
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }

  onAmountInput() {
    const raw = this.amountTarget.value.trim()
    if (this.hasExprResultTarget) {
      if (this.isExpression(raw)) {
        const result = this.evaluateExpression(raw)
        this.exprResultTarget.textContent = result !== null ? `= ${result}` : ""
      } else {
        this.exprResultTarget.textContent = ""
      }
    }
    this.updateConversion()
  }

  async submit(event) {
    event.preventDefault()

    const amount = this.getAmount()
    if (!amount || amount <= 0) {
      this.shake(this.amountTarget)
      return
    }

    if (this.kind === "transfer") {
      return this.submitTransfer(amount)
    }

    if (!this.selectedCategoryId && !this.selectedDebtId) {
      const msg = this.pickerMode === "debt" ? t("add_transaction.select_debt") : t("add_transaction.select_category")
      alert(msg)
      return
    }

    this.saveBtnTarget.style.opacity = "0.6"
    this.saveBtnTarget.style.pointerEvents = "none"

    try {
      const txData = {
        amount: amount,
        category_id: this.selectedCategoryId,
        kind: this.kind,
        note: this.hasNoteTarget ? this.noteTarget.value : "",
        date: new Date().toISOString().slice(0, 10),
        account_id: this.selectedAccountId
      }
      let result
      if (this.selectedDebtId) {
        // Debt-linked transactions require online — skip offline queue
        if (!networkStatus.online) { alertOffline(); this.saveBtnTarget.style.opacity = "1"; this.saveBtnTarget.style.pointerEvents = "auto"; return }
        result = await apiPost("/api/transactions", { transaction: txData, debt_id: this.selectedDebtId })
      } else {
        result = await createTransactionOffline(txData)
      }

      if (result.pending) {
        this.showOfflineSuccess(result)
        return
      }

      if (result.warning === "duplicate_suspected") {
        this.saveBtnTarget.style.opacity = "1"
        this.saveBtnTarget.style.pointerEvents = "auto"
        const confirmed = confirm(t("add_transaction.duplicate_warning"))
        if (confirmed) {
          this.saveBtnTarget.style.opacity = "0.6"
          this.saveBtnTarget.style.pointerEvents = "none"
          const forced = await apiPost("/api/transactions", { transaction: txData, force: true })
          this.showSuccess(forced)
        }
        return
      }

      this.showSuccess(result)
    } catch (e) {
      console.error("Submit error:", e)
      if (e.status === 403) {
        window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "transactions" } }))
      } else {
        alert(t("add_transaction.save_error"))
      }
      this.saveBtnTarget.style.opacity = "1"
      this.saveBtnTarget.style.pointerEvents = "auto"
    }
  }

  async submitTransfer(amount) {
    if (!networkStatus.online) { alertOffline(); return }
    const fromId = this.transferFromId
    const toId = this.transferToId

    if (!fromId || !toId || fromId === toId) {
      return
    }

    this.saveBtnTarget.style.opacity = "0.6"
    this.saveBtnTarget.style.pointerEvents = "none"

    try {
      await apiPost("/api/accounts/transfer", {
        from_account_id: fromId,
        to_account_id: toId,
        amount: amount,
        input_currency: this.transferInputCurrency
      })

      if (this.hasSuccessOverlayTarget && this.hasSuccessTextTarget) {
        const fromAcc = this.accounts.find(a => a.id === fromId)
        const toAcc = this.accounts.find(a => a.id === toId)
        const fromAmount = this.convertAmount(amount, this.transferInputCurrency, fromAcc?.currency)
        const toAmount = this.convertAmount(amount, this.transferInputCurrency, toAcc?.currency)
        this.successTextTarget.innerHTML = `
          <div>${fromAcc?.icon} -${formatMoney(fromAmount, fromAcc?.currency)}</div>
          <div style="color: rgb(148, 163, 184); font-size: 14px;">→</div>
          <div>${toAcc?.icon} +${formatMoney(toAmount, toAcc?.currency)}</div>`
        this.successOverlayTarget.classList.remove("hidden")

        setTimeout(() => {
          window.location.href = "/pwa"
        }, 800)
      } else {
        window.location.href = "/pwa"
      }
    } catch (e) {
      console.error("Transfer error:", e)
      if (e.status === 403) {
        window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "transfers" } }))
      } else {
        alert(t("add_transaction.save_error"))
      }
      this.saveBtnTarget.style.opacity = "1"
      this.saveBtnTarget.style.pointerEvents = "auto"
    }
  }

  showOfflineSuccess(result) {
    if (this.hasSuccessOverlayTarget && this.hasSuccessTextTarget) {
      const cat = (this.categories || []).find(c => c.id === this.selectedCategoryId)
      const icon = cat ? cat.icon : ""
      const name = cat ? cat.name : ""
      const acc = this.accounts.find(a => a.id === this.selectedAccountId)
      this.successTextTarget.innerHTML = `
        ${icon} ${name} — ${formatMoney(this.getAmount(), acc?.currency)}
        <div class="text-xs text-slate-400 mt-1">${t("add_transaction.saved_offline")}</div>`
      this.successOverlayTarget.classList.remove("hidden")

      // Don't redirect — stay on page and reset form
      this.redirectTimer = setTimeout(() => {
        this.resetForm()
      }, 1500)
    } else {
      this.resetForm()
    }
  }

  showSuccess(result) {
    this.lastTransactionId = result.id

    if (this.hasSuccessOverlayTarget && this.hasSuccessTextTarget) {
      const acc = this.accounts.find(a => a.id === this.selectedAccountId)
      let label
      if (this.selectedDebtId) {
        const debt = this.debts.find(d => d.id === this.selectedDebtId)
        label = debt ? debt.person_name : ""
      } else {
        const cat = (this.categories || []).find(c => c.id === this.selectedCategoryId)
        const icon = cat ? cat.icon : ""
        const name = cat ? cat.name : ""
        label = `${icon} ${name}`
      }
      this.successTextTarget.textContent = `${label} — ${formatMoney(this.getAmount(), acc?.currency)}`
      this.successOverlayTarget.classList.remove("hidden")

      if (this.hasUndoBtnTarget) {
        this.undoBtnTarget.classList.remove("hidden")
      }

      this.undoSecondsLeft = 2
      this.updateUndoTimer()
      this.undoCountdown = setInterval(() => {
        this.undoSecondsLeft--
        this.updateUndoTimer()
        if (this.undoSecondsLeft <= 0) {
          clearInterval(this.undoCountdown)
          this.undoCountdown = null
        }
      }, 1000)

      this.redirectTimer = setTimeout(() => {
        window.location.href = "/pwa"
      }, 2000)
    } else {
      window.location.href = "/pwa"
    }
  }

  updateUndoTimer() {
    if (this.hasUndoTimerTarget) {
      if (this.undoSecondsLeft > 0) {
        this.undoTimerTarget.classList.remove("hidden")
        this.undoTimerTarget.textContent = `${this.undoSecondsLeft}...`
      } else {
        this.undoTimerTarget.classList.add("hidden")
      }
    }
  }

  async undoTransaction() {
    if (!networkStatus.online) { alertOffline(); return }
    if (!this.lastTransactionId) return

    if (this.undoCountdown) clearInterval(this.undoCountdown)
    if (this.redirectTimer) clearTimeout(this.redirectTimer)

    try {
      await apiDelete(`/api/transactions/${this.lastTransactionId}`)

      if (this.hasSuccessTextTarget) {
        this.successTextTarget.textContent = t("add_transaction.undone")
      }
      if (this.hasUndoBtnTarget) this.undoBtnTarget.classList.add("hidden")
      if (this.hasUndoTimerTarget) this.undoTimerTarget.classList.add("hidden")

      const overlay = this.successOverlayTarget
      const inner = overlay.querySelector(".success-pop")
      if (inner) inner.querySelector(".text-5xl").textContent = "\u21a9"

      setTimeout(() => {
        this.resetForm()
      }, 1200)
    } catch (e) {
      console.error("Undo error:", e)
    }
  }

  resetForm() {
    this.lastTransactionId = null
    this.selectedCategoryId = null
    this.selectedDebtId = null
    if (this.hasAmountTarget) this.amountTarget.value = ""
    if (this.hasNoteTarget) this.noteTarget.value = ""
    if (this.hasExprResultTarget) this.exprResultTarget.textContent = ""
    if (this.hasSuccessOverlayTarget) this.successOverlayTarget.classList.add("hidden")
    if (this.hasUndoBtnTarget) this.undoBtnTarget.classList.add("hidden")
    if (this.hasUndoTimerTarget) this.undoTimerTarget.classList.add("hidden")
    if (this.hasSaveBtnTarget) {
      this.saveBtnTarget.style.opacity = "1"
      this.saveBtnTarget.style.pointerEvents = "auto"
    }
    if (this.pickerMode === "category") {
      this.renderCategories()
    } else {
      this.renderDebts()
    }
  }

  shake(el) {
    el.classList.add("animate-shake")
    setTimeout(() => el.classList.remove("animate-shake"), 400)
  }
}
